import { azureSttBackend } from "./azureSttBackend";
import { gradiumSttBackend } from "./gradiumSttBackend";
import type { SttBackend, SttBackendName } from "./types";
import { isSttBackendName } from "./types";
import { webSpeechSttBackend } from "./webSpeechSttBackend";

export type { SttBackend, SttBackendName, SttRecognitionOptions } from "./types";

export const STT_BACKEND_PREFERENCE_KEY = "wixar_stt_backend";
const STT_BACKEND_QUERY_PARAM = "stt";

const BACKEND_LADDER: readonly SttBackend[] = [
  webSpeechSttBackend,
  azureSttBackend,
  gradiumSttBackend,
];

let resolved: SttBackend | null = null;
let inflight: Promise<SttBackend | null> | null = null;

export function readPreferredSttBackendName(): SttBackendName | null {
  if (typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get(STT_BACKEND_QUERY_PARAM);
  if (isSttBackendName(fromUrl)) {
    localStorage.setItem(STT_BACKEND_PREFERENCE_KEY, fromUrl);
    return fromUrl;
  }
  const stored = localStorage.getItem(STT_BACKEND_PREFERENCE_KEY);
  return isSttBackendName(stored) ? stored : null;
}

async function resolveBackend(): Promise<SttBackend | null> {
  const preferred = readPreferredSttBackendName();
  const ordered = preferred
    ? [
        ...BACKEND_LADDER.filter((b) => b.name === preferred),
        ...BACKEND_LADDER.filter((b) => b.name !== preferred),
      ]
    : BACKEND_LADDER;
  for (const backend of ordered) {
    if (await backend.isSupported()) return backend;
  }
  return null;
}

export async function getSttBackend(): Promise<SttBackend> {
  if (resolved) return resolved;
  if (!inflight) {
    inflight = resolveBackend().then((backend) => {
      resolved = backend;
      return backend;
    });
  }
  const backend = await inflight;
  if (!backend) throw new Error("No STT backend available");
  return backend;
}

export async function isSttSupported(): Promise<boolean> {
  if (resolved) return true;
  if (!inflight) {
    inflight = resolveBackend().then((backend) => {
      resolved = backend;
      return backend;
    });
  }
  return (await inflight) !== null;
}

export function getResolvedBackendName(): SttBackendName | null {
  return resolved?.name ?? null;
}
